import type { DashboardDefinition } from "./DashboardDefinition";

// Wire values from the backend `DashboardAccessibility` StrEnum: the Pydantic
// validator accepts the enum *value* (lowercase), not the Python attribute
// name. Keep these as the literal strings the server expects.
//
// On create/update requests this is a knob the server folds into
// `owner_principal_id`. On `DashboardRecord` it is a derived computed field
// returned by the API — always in sync with the owner principal.
export const DASHBOARD_ACCESSIBILITIES = ["organization", "user"] as const;
export type DashboardAccessibility = (typeof DASHBOARD_ACCESSIBILITIES)[number];

export type { DashboardDefinition };

export interface DashboardRecord {
  /**
   * Derived from `owner_principal_id`: `"organization"` when owned by the org
   * principal, `"user"` otherwise. Returned by the API as a computed field.
   */
  accessibility: DashboardAccessibility;
  created: string;
  /**
   * User ID of the dashboard's creator. Audit metadata only — ownership (who may
   * rename, update, or delete the dashboard) is carried by `owner_principal_id`.
   */
  created_by: string;
  /**
   * The stored dashboard layout, as raw JSONB off the wire. Typed `unknown`
   * because the record may predate (or postdate) the current
   * `DashboardDefinition` schema: consumers must validate with
   * `isDashboardDefinition` and treat anything else as invalid input.
   *
   * This is the ONLY place a dashboard's schema version lives. The backend
   * stores the definition verbatim and never parses it, so `schema_version`
   * inside this document is the single source of truth — there is no
   * record-level counterpart that could disagree with it.
   */
  dashboard_definition: unknown;
  dashboard_id: string;
  modified: string;
  modified_by: string;
  name: string;
  org_id: string;
  /**
   * Principal that owns the dashboard, serialized as `ptype:id`: `org:<org_id>` for
   * organization-wide dashboards, `user:<user_id>` for personal ones. This is the sole
   * source of truth for org-wide vs personal — a dashboard is org-wide exactly when
   * its owner is the org principal. Dashboard names are unique per owner within an
   * organization.
   */
  owner_principal_id: string;
  /**
   * How many times the definition has been written, starting at 0.
   *
   * A definition-generation counter, not a row version: it advances only when
   * `dashboard_definition` is replaced, and a rename or accessibility change
   * deliberately leaves it alone. Echo the value you loaded back as
   * `base_revision` when saving a definition, so a save built on a stale copy is
   * rejected instead of silently erasing someone else's edit.
   */
  revision: number;
}

export interface CreateDashboardRequest {
  accessibility: DashboardAccessibility;
  dashboard_definition: DashboardDefinition;
  name: string;
}

/** Fields that never touch the definition, and so never carry a base revision. */
type DashboardMetadataUpdate = Partial<{
  accessibility: DashboardAccessibility;
  name: string;
}>;

/**
 * A definition write MUST carry `base_revision` — the revision the requester
 * loaded — and a metadata-only update must NOT. The union makes both halves of
 * that rule a compile error rather than a convention, mirroring the server's
 * validator, which rejects each violation in turn.
 */
export type UpdateDashboardRequest =
  | (DashboardMetadataUpdate & {
      dashboard_definition: DashboardDefinition;
      base_revision: number;
    })
  | (DashboardMetadataUpdate & {
      dashboard_definition?: never;
      base_revision?: never;
    });

/**
 * Structural check for a `DashboardRecord` arriving from outside the type system
 * — specifically the record carried by a revision-conflict response body, which
 * is typed `unknown`. A payload that fails this must take the caller's ordinary
 * error path rather than driving conflict UI.
 */
export function isDashboardRecord(value: unknown): value is DashboardRecord {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const record = value as Record<string, unknown>;
  return (
    typeof record.dashboard_id === "string" &&
    typeof record.name === "string" &&
    typeof record.org_id === "string" &&
    typeof record.owner_principal_id === "string" &&
    typeof record.revision === "number" &&
    record.dashboard_definition !== undefined
  );
}
